// The page's weight under a mouse wheel, and every animated jump on it.
//
// On a fine pointer, one wheel tick travels 60% of its delta and approaches the
// target exponentially at 3 per second, settling in about 2s. Hash links and
// programmatic jumps share the same easing and respect the scroll padding.
//
// It stays out of the way of: touch (never hijacked), reduced motion (jumps are
// instant), trackpad pinch (ctrl+wheel), a horizontal wheel, a nested scroller
// that can still move, and a page locked by the phone menu or a modal. If
// something else moves the page mid-flight (keyboard, scrollbar, a focus jump),
// the animation lets go and the next wheel tick starts from where the page is.
//
// BODY is the scroller, so the listeners are on document and the position is
// body.scrollTop.

import { useEffect } from "react";
import { FINE_POINTER, getScroller, getScrollTop, prefersReducedMotion } from "./env";

const WHEEL_MULTIPLIER = 0.6;
const RATE = 3; // per second
const SETTLE_PX = 0.5;
const DRIFT_PX = 4;

const state = { target: 0, current: 0, written: 0, last: 0, raf: 0 };

const maxScroll = () => {
    const s = getScroller();
    return Math.max(s.scrollHeight - s.clientHeight, 0);
};

const stop = () => {
    cancelAnimationFrame(state.raf);
    state.raf = 0;
};

const tick = (now) => {
    const s = getScroller();
    if (Math.abs(s.scrollTop - state.written) > DRIFT_PX) {
        stop();
        return;
    }
    const dt = Math.min((now - state.last) / 1000, 0.1);
    state.last = now;
    state.current += (state.target - state.current) * (1 - Math.exp(-RATE * dt));
    if (Math.abs(state.target - state.current) < SETTLE_PX) state.current = state.target;
    s.scrollTop = state.current;
    state.written = s.scrollTop;
    if (state.current === state.target) {
        stop();
        return;
    }
    state.raf = requestAnimationFrame(tick);
};

const animateTo = (y) => {
    const s = getScroller();
    state.target = Math.min(Math.max(y, 0), maxScroll());
    if (state.raf) return;
    state.current = s.scrollTop;
    state.written = s.scrollTop;
    state.last = performance.now();
    state.raf = requestAnimationFrame(tick);
};

// Scroll the page to `y` with the page's easing.
export function smoothScrollTo(y) {
    const s = getScroller();
    if (prefersReducedMotion()) {
        stop();
        s.scrollTop = y;
        return;
    }
    if (window.matchMedia?.(FINE_POINTER)?.matches) {
        animateTo(y);
        return;
    }
    s.scrollTo({ top: y, behavior: "smooth" });
}

// Scroll to an element as an anchor would: below the fixed header, honouring
// its own scroll-margin-top.
export function scrollToElement(el) {
    const padding = parseFloat(getComputedStyle(getScroller()).scrollPaddingTop) || 0;
    const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    smoothScrollTo(getScrollTop() + el.getBoundingClientRect().top - padding - margin);
}

const canScrollInside = (from, dy) => {
    const body = getScroller();
    for (let el = from; el && el !== body && el.nodeType === 1; el = el.parentElement) {
        const overflow = getComputedStyle(el).overflowY;
        if ((overflow === "auto" || overflow === "scroll") && el.scrollHeight > el.clientHeight + 1) {
            const atEdge = dy > 0
                ? el.scrollTop + el.clientHeight >= el.scrollHeight - 1
                : el.scrollTop <= 0;
            if (!atEdge) return true;
        }
    }
    return false;
};

const wheelPixels = (e) => {
    if (e.deltaMode === 1) return e.deltaY * 16;
    if (e.deltaMode === 2) return e.deltaY * window.innerHeight;
    return e.deltaY;
};

// Mount once, on the page. Installs the wheel and the hash links under `rootRef`.
export function useSmoothScroll(rootRef) {
    useEffect(() => {
        const onWheel = (e) => {
            if (e.defaultPrevented || e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
            if (document.body.classList.contains("ax-modal-open")) return;
            if (prefersReducedMotion() || !window.matchMedia?.(FINE_POINTER)?.matches) return;
            if (canScrollInside(e.target, e.deltaY)) return;
            e.preventDefault();
            const from = state.raf ? state.target : getScrollTop();
            animateTo(from + wheelPixels(e) * WHEEL_MULTIPLIER);
        };

        const onClick = (e) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const a = e.target.closest?.('a[href^="#"]');
            if (!a || !rootRef?.current?.contains(a)) return;
            const hash = a.getAttribute("href");
            if (hash === "#" || hash === "#top") {
                e.preventDefault();
                smoothScrollTo(0);
                window.history.replaceState(null, "", window.location.pathname + window.location.search);
                return;
            }
            const target = document.getElementById(decodeURIComponent(hash.slice(1)));
            if (!target) return;
            e.preventDefault();
            scrollToElement(target);
            window.history.pushState(null, "", hash);
            if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
            target.focus({ preventScroll: true });
        };

        document.addEventListener("wheel", onWheel, { passive: false });
        document.addEventListener("click", onClick);
        return () => {
            document.removeEventListener("wheel", onWheel);
            document.removeEventListener("click", onClick);
            stop();
        };
    }, [rootRef]);
}
